import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Descriptions,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  Progress,
  Space,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  CheckCircleFilled,
  CodeOutlined,
  ExperimentOutlined,
  FileSearchOutlined,
  ReloadOutlined,
  RobotOutlined,
  SafetyCertificateOutlined,
  SendOutlined,
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  AgentFactoryAutoTestInput,
  AgentFactoryOptions,
  AgentFactoryProjectStatus,
  confirmAgentFactoryDesign,
  submitAgentFactoryDelivery,
} from '../../services/agentFactoryApi';
import './agent-factory-workspace.css';

const { Text, Title, Paragraph } = Typography;

interface Props {
  status: AgentFactoryProjectStatus;
  options?: AgentFactoryOptions | null;
  mode: 'design' | 'development';
  loading?: boolean;
  agentRunning?: boolean;
  onRefresh: () => Promise<void> | void;
  onConfirmed: () => void;
  onStartAutoTest: (input: AgentFactoryAutoTestInput) => Promise<void>;
}

function MarkdownView({ content, empty }: { content?: string; empty: string }) {
  if (!content) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={empty} />;
  return (
    <div className="af-markdown">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
    </div>
  );
}

function testStatus(run?: { status: string }) {
  if (run?.status === 'passed') return { color: 'success', text: '已通过' };
  if (run?.status === 'failed') return { color: 'error', text: '未通过' };
  if (run?.status === 'running') return { color: 'processing', text: '执行中' };
  return { color: 'default', text: '未测试' };
}

export default function AgentFactoryWorkspace({
  status,
  options,
  mode,
  loading,
  agentRunning,
  onRefresh,
  onConfirmed,
  onStartAutoTest,
}: Props) {
  const [designOpen, setDesignOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [testing, setTesting] = useState(false);
  const [delivering, setDelivering] = useState(false);
  const [testForm] = Form.useForm();
  const [deliveryForm] = Form.useForm();

  const latestRun = status.tests?.[0];
  const latestPassedRun = status.tests?.find(run => run.status === 'passed');
  const testMeta = testStatus(latestRun);
  const developmentReady = Boolean(
    status.development?.manifestReady &&
    status.development?.productionReadinessReady &&
    status.development?.dockerReady &&
    status.development?.environmentReady &&
    (!status.development?.visualQaRequired || status.development?.visualQaReady),
  );
  const channels = status.config?.experienceChannels || [];
  const domain = options?.domains.find(item => item.value === status.config?.domain);
  const dataLabels = (status.config?.dataSources || [])
    .map(value => options?.dataSources.find(item => item.value === value)?.label || value);
  const capabilityLabels = (status.config?.capabilities || [])
    .map(value => options?.capabilities.find(item => item.value === value)?.label || value);

  const lifecycle = useMemo(() => [
    { label: '方案产出', done: Boolean(status.design?.ready), active: mode === 'design' && !status.design?.ready },
    { label: '业务确认', done: status.design?.status === 'confirmed', active: status.design?.status === 'ready' || status.design?.status === 'stale' },
    { label: '智能开发', done: developmentReady, active: mode === 'development' && !developmentReady },
    { label: '质量验证', done: Boolean(latestPassedRun), active: latestRun?.status === 'running' },
    { label: '投产交付', done: ['submitted', 'approved', 'listed'].includes(status.delivery?.status || ''), active: false },
  ], [status, mode, latestRun, latestPassedRun, developmentReady]);

  const readinessPercent = Math.round(
    lifecycle.filter(item => item.done).length / lifecycle.length * 100,
  );

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await confirmAgentFactoryDesign(status.projectId);
      message.success('产品方案和技术方案已锁定，可以进入开发');
      await onRefresh();
      setDesignOpen(false);
      onConfirmed();
    } catch (error: any) {
      console.error('确认智能体方案失败:', error.response?.status, error.response?.data?.message || error.message);
      message.error(error.response?.data?.message || '方案确认失败');
    } finally {
      setConfirming(false);
    }
  };

  const handleAutoTest = async () => {
    const values = await testForm.validateFields();
    const seedQuestions = String(values.seedQuestions || '')
      .split(/\n+/)
      .map(item => item.trim())
      .filter(Boolean);
    setTesting(true);
    try {
      await onStartAutoTest({
        seedQuestions,
        generatedCount: values.generatedCount,
        maxEvolutionRounds: values.maxEvolutionRounds,
      });
      setTestOpen(false);
      message.success('自动测试已启动，执行进度会显示在当前开发会话');
    } catch (error: any) {
      console.error('启动智能体自动测试失败:', error.response?.status, error.response?.data?.message || error.message);
      message.error(error.response?.data?.message || '自动测试启动失败');
    } finally {
      setTesting(false);
    }
  };

  const handleDelivery = async () => {
    const values = await deliveryForm.validateFields();
    setDelivering(true);
    try {
      const result = await submitAgentFactoryDelivery(status.projectId, values);
      message.success(result.message || '投产交付已提交');
      await onRefresh();
      setDeliveryOpen(false);
    } catch (error: any) {
      console.error('提交智能体交付失败:', error.response?.status, error.response?.data?.message || error.message);
      message.error(error.response?.data?.message || '提交失败');
    } finally {
      setDelivering(false);
    }
  };

  const canConfirm = Boolean(status.design?.ready) && status.design?.status !== 'confirmed';
  const canTest = mode === 'development' &&
    status.design?.status === 'confirmed' &&
    developmentReady &&
    !agentRunning;
  const deliverySubmitted = ['submitted', 'approved', 'listed'].includes(status.delivery?.status || '');
  const canDeliver = Boolean(
    status.design?.status === 'confirmed' &&
    developmentReady &&
    latestPassedRun &&
    !agentRunning &&
    !deliverySubmitted,
  );

  return (
    <>
      <section className="af-workspace-band" aria-label="智能体工坊状态">
        <div className="af-workspace-heading">
          <span className="af-workspace-icon"><RobotOutlined /></span>
          <div>
            <div className="af-workspace-eyebrow">高速公路行业智能体工坊</div>
            <div className="af-workspace-title">
              {status.projectName}
              <Tag color="blue">{domain?.label || status.config?.domain || '综合场景'}</Tag>
            </div>
            <div className="af-workspace-summary">
              {status.businessGoal || '描述业务需求后，AI 将按行业架构完成设计与开发。'}
            </div>
          </div>
        </div>

        <div className="af-lifecycle">
          <div className="af-lifecycle-track">
            {lifecycle.map((item, index) => (
              <div
                key={item.label}
                className={`af-lifecycle-item ${item.done ? 'is-done' : ''} ${item.active ? 'is-active' : ''}`}
              >
                <span>{item.done ? <CheckCircleFilled /> : index + 1}</span>
                <b>{item.label}</b>
              </div>
            ))}
          </div>
          <Progress percent={readinessPercent} size="small" showInfo={false} />
        </div>

        <div className="af-workspace-actions">
          <Tooltip title="重新读取设计、开发和测试产物">
            <Button icon={<ReloadOutlined />} loading={loading} onClick={onRefresh} aria-label="刷新智能体状态" />
          </Tooltip>
          <Button icon={<FileSearchOutlined />} onClick={() => setDesignOpen(true)}>
            查看方案
          </Button>
          {mode === 'design' ? (
            <Button
              type="primary"
              icon={<SafetyCertificateOutlined />}
              disabled={!canConfirm}
              loading={confirming}
              onClick={handleConfirm}
            >
              {status.design?.status === 'confirmed' ? '方案已确认' : '确认方案'}
            </Button>
          ) : (
            <>
              <Button
                icon={<ExperimentOutlined />}
                disabled={!canTest}
                onClick={() => setTestOpen(true)}
              >
                自动测试
              </Button>
              <Button
                type="primary"
                icon={<SendOutlined />}
                disabled={!canDeliver}
                onClick={() => {
                  deliveryForm.setFieldsValue({
                    serviceBaseUrl: status.delivery?.serviceBaseUrl,
                    standaloneUrl: status.delivery?.standaloneUrl,
                  });
                  setDeliveryOpen(true);
                }}
              >
                {status.delivery?.status === 'listed'
                  ? '已投产'
                  : deliverySubmitted
                    ? '审核处理中'
                    : '投产交付'}
              </Button>
            </>
          )}
        </div>
      </section>

      {status.design?.status === 'stale' && (
        <Alert
          className="af-workspace-alert"
          type="warning"
          showIcon
          message="确认后的设计文件发生了变化"
          description="请重新查看产品方案和技术方案并再次确认，开发入口已自动锁定。"
        />
      )}

      {mode === 'development' && Boolean(status.development?.validationErrors?.length) && (
        <Alert
          className="af-workspace-alert"
          type="info"
          showIcon
          message="投产产物仍在生成或需要修正"
          description={status.development?.validationErrors?.join('；')}
        />
      )}

      <Drawer
        title="智能体设计方案"
        open={designOpen}
        onClose={() => setDesignOpen(false)}
        width={920}
        className="af-design-drawer"
        extra={
          <Button
            type="primary"
            icon={<SafetyCertificateOutlined />}
            disabled={!canConfirm}
            loading={confirming}
            onClick={handleConfirm}
          >
            确认并进入开发
          </Button>
        }
      >
        {!status.design?.ready && (
          <Alert
            type="info"
            showIcon
            message="设计方案尚在生成"
            description={`仍需完成：${[
              ...(status.design?.missingFiles || []),
              ...(status.design?.validationErrors || []),
            ].join('；') || '设计产物'}`}
            style={{ marginBottom: 16 }}
          />
        )}
        <Tabs
          items={[
            {
              key: 'product',
              label: '产品视图',
              children: <MarkdownView content={status.design?.productDesign} empty="AI 完成设计后，这里会展示面向业务和产品人员的方案。" />,
            },
            {
              key: 'technical',
              label: '技术视图',
              children: <MarkdownView content={status.design?.technicalDesign} empty="AI 完成设计后，这里会展示架构、数据、Skill 和安全设计。" />,
            },
            ...(status.design?.visualDesign ? [{
              key: 'visual',
              label: '体验视图',
              children: <MarkdownView content={status.design?.visualDesign} empty="独立 Web 的体验设计尚未生成。" />,
            }] : []),
            ...(mode === 'development' && status.development?.visualQaRequired ? [{
              key: 'visual-qa',
              label: '视觉验收',
              children: (
                <div>
                  <Alert
                    type={status.development?.visualQaReady ? 'success' : 'warning'}
                    showIcon
                    message={status.development?.visualQaReady
                      ? `真实视觉复检已通过（${status.development?.visualAttestation?.reviewedImageCount || 0} 张图片）`
                      : '尚未取得有效的前后截图复检签名'}
                    description={status.development?.visualQaReady
                      ? `平台签名时间：${status.development?.visualAttestation?.verifiedAt || '-'}`
                      : '开发智能体必须用真实浏览器截图，并通过 image_read 查看修复前后画面。'}
                    style={{ marginBottom: 16 }}
                  />
                  <MarkdownView
                    content={status.development?.visualQaReport}
                    empty="视觉验收报告尚未生成。"
                  />
                </div>
              ),
            }] : []),
            {
              key: 'scope',
              label: '范围配置',
              children: (
                <div className="af-scope-view">
                  <Descriptions column={1} bordered size="small">
                    <Descriptions.Item label="业务目标">{status.businessGoal || '-'}</Descriptions.Item>
                    <Descriptions.Item label="业务域">{domain?.label || status.config?.domain}</Descriptions.Item>
                    <Descriptions.Item label="数据源">
                      <Space size={[4, 6]} wrap>{dataLabels.map(item => <Tag key={item}>{item}</Tag>)}</Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="交付入口">
                      {channels.map(value => (
                        <Tag key={value} color="cyan">
                          {options?.channels.find(item => item.value === value)?.label || value}
                        </Tag>
                      ))}
                    </Descriptions.Item>
                    <Descriptions.Item label="能力">
                      <Space size={[4, 6]} wrap>{capabilityLabels.map(item => <Tag key={item}>{item}</Tag>)}</Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="设计指纹">
                      <Text code copyable>{status.design?.hash || '尚未生成'}</Text>
                    </Descriptions.Item>
                  </Descriptions>
                </div>
              ),
            },
          ]}
        />
      </Drawer>

      <Modal
        title={<Space><ExperimentOutlined />智能体自动测试</Space>}
        open={testOpen}
        onCancel={() => setTestOpen(false)}
        onOk={handleAutoTest}
        okText="开始真实链路测试"
        confirmLoading={testing}
        width={720}
      >
        <Paragraph type="secondary">
          AI 会从需求和种子问题扩展用例，调用真实智能体链路验证；缺陷将先沉淀为永久回归用例，再在候选版本中修复并全量复测。
        </Paragraph>
        <Form
          form={testForm}
          layout="vertical"
          initialValues={{ generatedCount: 50, maxEvolutionRounds: 3 }}
        >
          <Form.Item name="seedQuestions" label="补充种子问题">
            <Input.TextArea rows={6} placeholder="每行一个真实业务问题；不填则由 AI 根据已确认方案生成。" />
          </Form.Item>
          <div className="af-form-grid">
            <Form.Item name="generatedCount" label="目标衍生问题数" rules={[{ required: true }]}>
              <InputNumber min={10} max={200} step={10} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="maxEvolutionRounds" label="最大自动进化轮数" rules={[{ required: true }]}>
              <InputNumber min={1} max={5} style={{ width: '100%' }} />
            </Form.Item>
          </div>
        </Form>
        <div className="af-test-history">
          <Title level={5}>最近测试</Title>
          {!status.tests?.length ? (
            <Text type="secondary">暂无测试记录</Text>
          ) : (
            <List
              size="small"
              dataSource={status.tests.slice(0, 5)}
              renderItem={run => {
                const meta = testStatus(run);
                return (
                  <List.Item>
                    <Space>
                      <Tag color={meta.color}>{meta.text}</Tag>
                      <Text code>{run.runId}</Text>
                    </Space>
                    <Text type="secondary">{run.passedCases}/{run.totalCases || '-'} 通过</Text>
                  </List.Item>
                );
              }}
            />
          )}
        </div>
      </Modal>

      <Modal
        title={<Space><CodeOutlined />投产交付</Space>}
        open={deliveryOpen}
        onCancel={() => setDeliveryOpen(false)}
        onOk={handleDelivery}
        okText={channels.includes('devforge_cockpit') ? '提交管理员接入' : '登记交付'}
        confirmLoading={delivering}
        width={680}
      >
        <div className="af-gate-summary">
          <div><CheckCircleFilled /> 设计方案已确认</div>
          <div><CheckCircleFilled /> Manifest、容器、环境样例与投产清单已生成</div>
          <div><CheckCircleFilled /> 自动测试已通过</div>
        </div>
        <Form form={deliveryForm} layout="vertical">
          {channels.includes('devforge_cockpit') && (
            <>
              <Form.Item
                name="serviceBaseUrl"
                label="智能体真实服务 URL"
                rules={[{ required: true, message: '请输入可访问的智能体服务 URL' }, { type: 'url', message: 'URL 格式不正确' }]}
                extra="提交前会真实调用 /devforge-agent/v1/health；通过后进入管理员审核。"
              >
                <Input placeholder="https://agent.example.com" />
              </Form.Item>
              <Form.Item
                name="serviceToken"
                label="服务鉴权密钥"
                extra="可选。仅提交到安全后端，不会保存在项目配置或返回到页面。"
              >
                <Input.Password placeholder="与智能体服务约定的 HS256 密钥" autoComplete="new-password" />
              </Form.Item>
            </>
          )}
          {channels.includes('standalone_web') && (
            <Form.Item
              name="standaloneUrl"
              label="独立 Web 真实访问 URL"
              rules={[{ required: true, message: '请输入独立 Web 访问 URL' }, { type: 'url', message: 'URL 格式不正确' }]}
            >
              <Input placeholder="https://your-agent.example.com" />
            </Form.Item>
          )}
        </Form>
        {status.delivery?.status && status.delivery.status !== 'not_started' && (
          <Alert
            type="success"
            showIcon
            message={`当前交付状态：${status.delivery.status}`}
            description={status.delivery.agentCode ? `智能体编码：${status.delivery.agentCode}` : undefined}
          />
        )}
      </Modal>

      <span className="af-test-status-a11y" aria-live="polite">
        自动测试状态：{testMeta.text}
      </span>
    </>
  );
}
